import { useCallback, useEffect, useReducer, useState } from 'react';
import { useNavigate, useSearchParams, type NavigateFunction } from 'react-router-dom';
import { Banner, Button, EmptyState, Tabs, TabPanel } from '@immo/ui-kit';
import { FLEET_BOARD_ALL_VIEW, type FleetBoardLane, type GetFleetBoardResponse } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { NetworkError } from '@/lib/errors/network-error';
import { useTranslations } from '@/i18n/use-translations';
import { useLocale } from '@/i18n/provider';
import { AlertCircleIcon, ServerIcon, SparkleIcon, WarningTriangleIcon } from '@/components/layout/icons';
import { BreadcrumbLeading, ListLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { BoardSkeleton } from '@/components/fleet/board/board-skeleton';
import { BoardErrorState } from '@/components/fleet/board/board-error-state';
import { FleetBoard } from '@/components/fleet/board/fleet-board';
import { AssistantPanel } from '@/components/fleet/board/assistant-panel';
import { TicketPanel } from '@/components/fleet/board/ticket-panel';
import { WorkersPanel } from '@/components/fleet/board/workers-panel';
import { useQueueRanking } from '@/components/fleet/board/use-queue-ranking';
import { useCan } from '@/lib/auth/permissions';
import { findCard } from '@/lib/fleet/board-queue';
import {
  FLEET_BOARD_POLL_INTERVAL_MS,
  fleetBoardPollReducer,
  INITIAL_FLEET_BOARD_POLL,
  isFleetBoardPageErrorCode,
  isStaleSnapshot,
} from '@/lib/fleet/board-poll';

const FORBIDDEN = 403;
const VIEW_PARAM = 'view';
/** The ticket whose details panel is open, by its Linear identifier: `?job=IMM-123`. */
const TICKET_PARAM = 'job';
/** The lane whose workers panel is open: `?workers=engineer`. One panel at a time — opening one closes the other. */
const WORKERS_PARAM = 'workers';
/** The assistant panel: `?assistant=1`. Same one-panel rule; ignored without the command permission. */
const ASSISTANT_PARAM = 'assistant';
const TABS_ID = 'fleet-board-view';

type ReadAt = { readonly delay: number; readonly n: number };

function errorCodeOf(body: unknown): string | undefined {
  return (body as { error?: { code?: string } } | undefined)?.error?.code;
}

function deny(navigate: NavigateFunction): void {
  void navigate('/access-denied', { replace: true, state: { reason: 'missing-permission' } });
}

/**
 * Reads one view's board on mount and 30 s after each answer — the backend's floor, so a faster poll would only
 * re-read the same snapshot. A timeout chain, never an interval, so a slow answer never stacks reads.
 */
function useBoardPoll(view: string) {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(fleetBoardPollReducer, INITIAL_FLEET_BOARD_POLL);
  const [readAt, setReadAt] = useState<ReadAt>({ delay: 0, n: 0 });

  useEffect(() => {
    let current = true;
    const next = () => setReadAt({ delay: FLEET_BOARD_POLL_INTERVAL_MS, n: readAt.n + 1 });

    const read = async () => {
      let result: Awaited<ReturnType<typeof apiClient.adminFleetBoard.get>>;

      try {
        result = await apiClient.adminFleetBoard.get({ query: { view } });
      } catch (cause) {
        if (current) {
          dispatch({ type: 'failed', code: undefined, ...(cause instanceof NetworkError ? { network: cause } : {}) });
          next();
        }

        return;
      }

      if (!current) return;

      if (!result.ok && result.status === FORBIDDEN) {
        deny(navigate);

        return;
      }

      dispatch(
        result.ok ? { type: 'answered', board: result.body } : { type: 'failed', code: errorCodeOf(result.body) },
      );
      next();
    };

    const timer = setTimeout(() => void read(), readAt.delay);

    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [readAt, view, navigate]);

  // A new `n` discards a read still in flight and reads now: after a move, whatever that read answers predates it.
  const reread = useCallback(() => setReadAt((previous) => ({ delay: 0, n: previous.n + 1 })), []);

  return { state, reread };
}

/** What the ranking hook is handed before the first board has answered. */
const NO_BOARD: GetFleetBoardResponse = { view: FLEET_BOARD_ALL_VIEW, views: [], fetchedAt: null, lanes: [] };

const cardsIn = (lanes: ReadonlyArray<FleetBoardLane>) =>
  lanes.reduce(
    (sum, lane) => sum + lane.rounds.length + lane.stranded.length + lane.queue.length + lane.untaken.length,
    0,
  );

/**
 * The fleet's lanes as a board, one tab per view (`all`, then each project): what `fleet-agent agent preview` shows
 * for one host, for every machine. Read-only — a ticket moves in Linear or through a lane, never here.
 */
export function FleetBoardPage() {
  const t = useTranslations('pages.admin.fleet-board');
  const [searchParams, setSearchParams] = useSearchParams();
  const [known, setKnown] = useState<ReadonlyArray<string> | null>(null);

  const requested = searchParams.get(VIEW_PARAM) ?? FLEET_BOARD_ALL_VIEW;
  // Which views exist is the backend's to say; until it has, the requested one is read, and an unknown one falls back.
  const view = known === null || known.includes(requested) ? requested : FLEET_BOARD_ALL_VIEW;
  const names = known ?? [FLEET_BOARD_ALL_VIEW];
  const key = (name: string) => `v${names.indexOf(name)}`;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex-none border-b border-border bg-surface px-4 pt-4 sm:px-6 sm:pt-5">
        <p className="mb-3 text-sm text-text-secondary">{t('subtitle')}</p>
        <Tabs
          idBase={TABS_ID}
          tabs={names.map((name) => ({ key: key(name), label: name === FLEET_BOARD_ALL_VIEW ? t('views.all') : name }))}
          active={names.includes(view) ? key(view) : key(FLEET_BOARD_ALL_VIEW)}
          onChange={(tab) =>
            setSearchParams(
              (previous) => {
                const next = new URLSearchParams(previous);
                next.set(VIEW_PARAM, names[Number(tab.slice(1))] as string);

                return next;
              },
              { replace: true },
            )
          }
          className="border-b-0"
        />
      </div>
      <TabPanel
        idBase={TABS_ID}
        tabKey={names.includes(view) ? key(view) : key(FLEET_BOARD_ALL_VIEW)}
        className="relative flex min-h-0 flex-1 flex-col"
      >
        {/* Keyed by view: a tab switch starts a fresh poll that reads at once, and never shows the other view's cards. */}
        <ViewBoard key={view} view={view} onViews={setKnown} />
      </TabPanel>
    </div>
  );
}

function ViewBoard({
  view,
  onViews,
}: {
  readonly view: string;
  readonly onViews: (views: ReadonlyArray<string>) => void;
}) {
  const t = useTranslations('pages.admin.fleet-board');
  const tErrors = useTranslations('errors');
  const { locale } = useLocale();
  const { state, reread } = useBoardPoll(view);
  const canCommand = useCan('backoffice:fleet:command');
  const [searchParams, setSearchParams] = useSearchParams();
  const ranking = useQueueRanking(state.status === 'loaded' ? state.board : NO_BOARD, reread);
  const views = state.status === 'loaded' ? state.board.views : null;
  const ticket = searchParams.get(TICKET_PARAM);
  const workersOf = searchParams.get(WORKERS_PARAM);
  const assistantOpen = canCommand && searchParams.get(ASSISTANT_PARAM) === '1';

  // One panel at a time: setting one parameter drops the other.
  const showPanel = useCallback(
    (param: string | null, value?: string, replace = true) =>
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          next.delete(TICKET_PARAM);
          next.delete(WORKERS_PARAM);
          next.delete(ASSISTANT_PARAM);

          if (param !== null && value !== undefined) next.set(param, value);

          return next;
        },
        { replace },
      ),
    [setSearchParams],
  );

  const open = useCallback(
    (card: { readonly identifier: string }) => showPanel(TICKET_PARAM, card.identifier),
    [showPanel],
  );
  const openTicket = useCallback((target: string) => showPanel(TICKET_PARAM, target, false), [showPanel]);
  const openWorkers = useCallback((lane: string) => showPanel(WORKERS_PARAM, lane), [showPanel]);
  const close = useCallback(() => showPanel(null), [showPanel]);

  useEffect(() => {
    if (views !== null) onViews(views);
  }, [views, onViews]);

  if (state.status === 'crashed') throw state.network ?? new Error('Failed to load the fleet board');

  // Over the board, in every state that has one: a deep link opens it before the first poll has answered. Its ticket
  // runs are re-read whenever the poll answers; opening it changes the search params only, never the poll.
  const panel = assistantOpen ? (
    <AssistantPanel onClose={close} />
  ) : workersOf !== null ? (
    <WorkersPanel
      lane={workersOf}
      refreshKey={state.status === 'loaded' ? state.board : null}
      canCommand={canCommand}
      onClose={close}
    />
  ) : ticket === null ? null : (
    <TicketPanel
      ticket={ticket}
      found={state.status === 'loaded' ? findCard(ranking.board, ticket) : null}
      refreshKey={state.status === 'loaded' ? state.board : null}
      canCommand={canCommand}
      onClose={close}
      onOpenTicket={openTicket}
    />
  );

  const topbar = (
    <PageTopbar
      leading={
        state.status === 'loaded' ? (
          <ListLeading title={t('title')} count={cardsIn(state.board.lanes)} icon={ServerIcon} />
        ) : (
          <BreadcrumbLeading crumbs={[{ label: t('title') }]} icon={ServerIcon} />
        )
      }
      trailing={
        canCommand ? (
          <Button
            type="button"
            size="sm"
            variant={assistantOpen ? 'primary' : 'secondary'}
            aria-pressed={assistantOpen}
            onClick={() => showPanel(assistantOpen ? null : ASSISTANT_PARAM, '1')}
          >
            <SparkleIcon aria-hidden className="h-4 w-4" />
            {t('assistant.open')}
          </Button>
        ) : undefined
      }
    />
  );

  if (state.status === 'loading') {
    return (
      <>
        {topbar}
        <BoardSkeleton />
        {panel}
      </>
    );
  }

  if (state.status === 'failed') {
    return (
      <>
        {topbar}
        <BoardErrorState code={state.code} />
        {panel}
      </>
    );
  }

  const { board, failure } = state;
  const now = Date.now();
  const stale = board.fetchedAt !== null && isStaleSnapshot(board.fetchedAt, now);

  return (
    <>
      {topbar}
      {failure !== null && (
        <div className="flex-none border-b border-border bg-surface px-4 py-3">
          <Banner
            variant="danger"
            icon={<AlertCircleIcon />}
            title={tErrors(
              failure !== undefined && isFleetBoardPageErrorCode(failure) ? failure : 'generic.unexpected',
            )}
          />
        </div>
      )}
      {failure === null && stale && board.fetchedAt !== null && (
        <div className="flex-none border-b border-border bg-surface px-4 py-3">
          <Banner
            variant="warning"
            icon={<WarningTriangleIcon />}
            title={t('stale', {
              time: new Date(board.fetchedAt).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }),
            })}
          />
        </div>
      )}
      {ranking.errorCode !== null && (
        <div className="flex-none border-b border-border bg-surface px-4 py-3">
          <Banner variant="danger" icon={<AlertCircleIcon />} title={tErrors(ranking.errorCode)} />
        </div>
      )}
      {board.lanes.length === 0 ? (
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <EmptyState title={t('no-lanes')} icon={<ServerIcon className="h-16 w-16" aria-hidden />} />
        </div>
      ) : (
        <div className="min-h-0 flex-1">
          <FleetBoard
            board={ranking.board}
            now={now}
            onOpen={open}
            onWorkers={openWorkers}
            ranking={canCommand ? ranking : undefined}
          />
        </div>
      )}
      {panel}
    </>
  );
}
